import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { User } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { UserStatus } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { useListRoles, useListUsers } from '@sunstone/api-client/hooks';
import {
  Badge,
  DataTable,
  PillFilter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useDebounce } from '@/hooks/useDebounce';
import { useListState } from '@/hooks/useListState';
import { routes } from '@/lib/routes';

dayjs.extend(relativeTime);

const PAGE_SIZE = 100;

const SORTABLE_COLUMNS = { name: 'Name', lastLogin: 'Last Active' };

const ALL_ROLES = '__all__';

function userStatusKey(status: UserStatus): string {
  switch (status) {
    case UserStatus.INVITED:
      return 'USER_STATUS_INVITED';
    case UserStatus.ACTIVE:
      return 'USER_STATUS_ACTIVE';
    case UserStatus.DISABLED:
      return 'USER_STATUS_DISABLED';
    case UserStatus.DELETED:
      return 'USER_STATUS_DELETED';
    default:
      return 'USER_STATUS_UNSPECIFIED';
  }
}

function userStatusFromKey(key: string | null): UserStatus {
  switch (key) {
    case 'USER_STATUS_INVITED':
      return UserStatus.INVITED;
    case 'USER_STATUS_ACTIVE':
      return UserStatus.ACTIVE;
    case 'USER_STATUS_DISABLED':
      return UserStatus.DISABLED;
    case 'USER_STATUS_DELETED':
      return UserStatus.DELETED;
    default:
      return UserStatus.UNSPECIFIED;
  }
}

function UserInitials({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-semibold flex-shrink-0">
      {initials || '?'}
    </span>
  );
}

function RoleCell({ user }: { user: User }) {
  if (user.primaryRoleId) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-foreground">{user.primaryRoleName || 'Role'}</span>
        <Badge
          variant="outline"
          className="text-[10px]"
          title={
            user.primaryRoleGrantsAll
              ? 'Auto-updates with new permissions'
              : 'Fixed permission list'
          }
        >
          {user.primaryRoleGrantsAll ? 'Always current' : 'Fixed list'}
        </Badge>
      </div>
    );
  }
  if (user.accessRuleCount > 0) {
    return (
      <span className="text-muted-foreground">Specific access only ({user.accessRuleCount})</span>
    );
  }
  return <span className="text-muted-foreground">No access</span>;
}

interface MemberListProps {
  scope: 'tenant' | 'platform_admins';
  tenantId?: string;
  /** Pin the list to one role (e.g. RoleDetailPage's Users tab): skips the
   * Role filter UI and its role-list fetch, using this id directly, and
   * drops the invite CTA (this is a read-only, role-scoped view). */
  fixedRoleId?: string;
  onRowClick?: (user: User) => void;
}

/**
 * Pure list: search, role filter, sort, pagination, row click-through. Every
 * create/invite CTA lives on the caller (TenantDetail's Users tab,
 * PlatformUsersPage) via PageHeader's action slot, matching how every other
 * top-level list (Customers, Partners) places its CTA — this component never
 * renders one itself.
 */
export function MemberList({ scope, tenantId = '', fixedRoleId, onRowClick }: MemberListProps) {
  const navigate = useNavigate();
  const { page, search, sortBy, sortDir, setPage, setSearch, setSort, getFilter, setFilter } =
    useListState(false);
  const statusFilter = getFilter('status');
  const roleIdFilter = fixedRoleId ?? getFilter('role') ?? '';

  const debouncedSearch = useDebounce(search);
  const { token } = useAuthToken();

  const { data: rolesData } = useListRoles(
    scope === 'tenant' && !fixedRoleId ? tenantId : '',
    token,
  );
  const roleFilterOptions = fixedRoleId
    ? []
    : (rolesData?.roles ?? []).map((r) => ({ value: r.id, label: r.name }));

  const { data, isLoading, error } = useListUsers(tenantId, token, {
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
    statusFilter: userStatusFromKey(statusFilter),
    roleIdFilter,
    platformAdminsOnly: scope === 'platform_admins',
    sortBy: sortBy === 'lastLogin' ? 'last_active' : sortBy === 'name' ? 'name' : 'created_at',
    sortDir: sortBy ? sortDir : 'desc',
  });

  const allUsers = data?.users ?? [];
  const totalCount = data?.totalCount ?? 0;

  const noun = fixedRoleId
    ? 'users with this role'
    : scope === 'tenant'
      ? 'members'
      : 'platform users';

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Failed to load {noun}.
      </div>
    );
  }

  if (!isLoading && allUsers.length === 0 && !search && !statusFilter && !roleIdFilter) {
    if (fixedRoleId) {
      return (
        <EmptyState
          icon={<Users className="w-8 h-8 text-muted-foreground/50" />}
          title="No users with this role"
          description="No one currently holds this role."
        />
      );
    }
    const inviteRoute =
      scope === 'tenant' ? routes.inviteMember(tenantId) : routes.invitePlatformUser();
    return (
      <EmptyState
        icon={<Users className="w-8 h-8 text-muted-foreground/50" />}
        title={scope === 'tenant' ? 'No members yet' : 'No platform users yet'}
        description={
          scope === 'tenant'
            ? 'Invite your first member to give them access to this customer.'
            : 'Invite a platform user to help manage Sunstone.'
        }
        actionLabel={scope === 'tenant' ? 'Invite member' : 'Invite platform user'}
        onAction={() => navigate(inviteRoute)}
      />
    );
  }

  const columns: ColumnDef<User>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <UserInitials name={row.original.name} />
          <span className="font-medium text-foreground">
            {row.original.name || row.original.email}
          </span>
          {row.original.status !== UserStatus.ACTIVE && (
            <StatusBadge status={userStatusKey(row.original.status)} />
          )}
        </div>
      ),
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.email}</span>,
    },
    scope === 'tenant'
      ? {
          id: 'role',
          header: 'Role',
          cell: ({ row }) => <RoleCell user={row.original} />,
        }
      : {
          id: 'role',
          header: 'Role',
          cell: ({ row }) => (
            <span className="text-foreground">{row.original.primaryRoleName || 'Role'}</span>
          ),
        },
    {
      id: 'lastLogin',
      header: 'Last Active',
      cell: ({ row }) =>
        row.original.status === UserStatus.INVITED ? (
          <span className="text-muted-foreground">Never logged in</span>
        ) : (
          <span className="text-muted-foreground">
            {row.original.lastActiveAt
              ? dayjs(timestampDate(row.original.lastActiveAt)).fromNow()
              : '—'}
          </span>
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={allUsers}
      showSearch
      searchPlaceholder="Search"
      searchValue={search}
      onSearchChange={setSearch}
      filterSlot={
        <>
          <PillFilter
            label="Status"
            value={statusFilter}
            onChange={(v) => setFilter('status', v)}
            options={[
              { value: 'USER_STATUS_ACTIVE', label: 'Active' },
              { value: 'USER_STATUS_INVITED', label: 'Pending' },
              { value: 'USER_STATUS_DISABLED', label: 'Disabled' },
              { value: 'USER_STATUS_DELETED', label: 'Deleted' },
            ]}
          />
          {roleFilterOptions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground font-medium">Role:</span>
              <Select
                value={roleIdFilter || ALL_ROLES}
                onValueChange={(v) => setFilter('role', v === ALL_ROLES ? null : v)}
              >
                <SelectTrigger className="h-7 w-[160px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_ROLES}>All roles</SelectItem>
                  {roleFilterOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      }
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={page}
      onPageChange={setPage}
      pageSize={PAGE_SIZE}
      onRowClick={onRowClick}
      manualSort={{ sortBy, sortDir, onSortChange: setSort, sortableColumns: SORTABLE_COLUMNS }}
    />
  );
}
